import React from 'react';
import { Link } from 'react-router-dom';

/**
 * OwnerPlatformBreakdown — platform booking counts for TurnKey Owner.
 * Brand palette v2.1: direct bookings use orange (#ff9f53); the count pill uses
 * a darkened orange so the white count keeps adequate contrast (large-text AA).
 * Props:
 *   platformBreakdown: { [platformName]: count }
 *   totalBookings: number
 */

const cardShell = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: 'clamp(16px, 5vw, 28px)',
  marginBottom: '24px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid #e8e8e8'
};

const heading = { margin: 0, color: '#333', fontSize: 'clamp(16px, 3.5vw, 18px)', fontWeight: '600' };

const OwnerPlatformBreakdown = ({ platformBreakdown, totalBookings }) => {
  const isEmpty = !platformBreakdown || Object.keys(platformBreakdown).length === 0;

  const palettes = {
    'Airbnb':          { bg: 'rgba(255, 99, 99, 0.06)',  border: 'rgba(255, 99, 99, 0.2)',     pill: 'rgba(233, 80, 80, 0.9)' },
    'Booking.com':     { bg: '#edf1f8',                   border: 'rgba(0, 46, 155, 0.12)',     pill: 'rgba(0, 46, 155, 0.9)' },
    'Gathern':         { bg: '#f2eefb',                   border: 'rgba(102, 51, 153, 0.2)',    pill: 'rgba(71, 19, 150, 0.9)' },
    'حجز مباشر':       { bg: '#fff3e8',                   border: 'rgba(255, 159, 83, 0.35)',   pill: '#e07a2f' }
  };

  return (
    <div dir="rtl" style={cardShell}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <h4 style={heading}>الحجوزات حسب المنصة</h4>
        {!isEmpty && (
          <Link
            to="/finance"
            style={{
              color: '#471396',
              textDecoration: 'none',
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px',
              minHeight: '44px'
            }}
          >
            عرض التفاصيل المالية
            <i className="bi bi-arrow-left"></i>
          </Link>
        )}
      </div>

      {isEmpty ? (
        <div style={{ textAlign: 'center', padding: 'clamp(24px, 6vw, 40px) 16px' }}>
          <i className="bi bi-pie-chart" style={{ fontSize: '32px', color: '#c9c5d6' }}></i>
          <p style={{ margin: '12px 0 4px', fontSize: '15px', fontWeight: '500', color: '#666' }}>
            لا توجد بيانات منصات بعد
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: '#999' }}>
            ستظهر الحجوزات حسب المنصة هنا عند استقبال أول حجز.
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          gap: '16px'
        }}>
          {Object.entries(platformBreakdown)
            .sort((a, b) => b[1] - a[1])
            .map(([platform, count]) => {
              const palette = palettes[platform] || palettes['Gathern'];
              return (
                <div key={platform} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  background: palette.bg,
                  borderRadius: '12px',
                  border: `1px solid ${palette.border}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: palette.pill,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}>
                    {count}
                  </div>
                  <div>
                    <h5 style={{ margin: 0, fontSize: '14px', color: '#333', fontWeight: '600' }}>
                      {platform}
                    </h5>
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      {((count / (totalBookings || 1)) * 100).toFixed(0)}% من الإجمالي
                    </p>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default OwnerPlatformBreakdown;
